import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTheme } from '@emotion/react';
import clsx from 'clsx';
import axios from 'axios';
import DateRangeSelector from '../../components/DateRangeSelector';
import { CallTableSkeleton } from '../../components/SkeletonLoader';

const columns = [
  { id: 'date', label: 'Date' },
  { id: 'time', label: 'Time' },
  { id: 'direction', label: 'Direction' },
  { id: 'callerName', label: 'Caller No.' },
  { id: 'callerNo', label: 'Reciever No.' },
  { id: 'startTime', label: 'Start Time' },
  { id: 'endTime', label: 'End Time' },
  { id: 'duration', label: 'Duration' },
  { id: 'actionPerformed', label: 'Action Performed' },
  { id: 'callForwardedTo', label: 'Call Forwarded To' },
];

const CallLogs = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [callLogs, setCallLogs] = useState([]);
  const [organizationId, setOrganizationId] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [prefetchedPages, setPrefetchedPages] = useState({});

  useEffect(() => {
    setOrganizationId(localStorage.getItem('userId'));
  }, []);

  useEffect(() => {
    if (!organizationId) return;
    if (prefetchedPages[page]) {
      setCallLogs(prefetchedPages[page]);
      setLoading(false);
    } else {
      setLoading(true);
      prefetchPages(page);
    }
    // eslint-disable-next-line
  }, [organizationId, dateRange, page]);

  const prefetchPages = async (pg) => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/dashboard/calllogs/${organizationId}`, {
        params: {
          startDate: dateRange.start,
          endDate: dateRange.end,
          page: pg,
          pageSize: pageSize
        }
      });
      // response.data.pages is an object: {1: [...], 2: [...], 3: [...]} etc.
      setPrefetchedPages(prev => ({ ...prev, ...response.data.pages }));
      setTotalPages(response.data.totalPages);
      setTotalCount(response.data.totalCount);
      setCallLogs(response.data.pages[pg] || []);
    } catch (error) {
      console.error('Error fetching call logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCallLogs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/dashboard/calllogs/${organizationId}`, {
        params: {
          startDate: dateRange.start,
          endDate: dateRange.end,
          page: page,
          pageSize: pageSize
        }
      });
      setCallLogs(response.data.logs);
      setTotalPages(response.data.totalPages);
      setTotalCount(response.data.totalCount);
    } catch (error) {
      console.error('Error fetching call logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
    setPage(1);
  };

  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      if (prefetchedPages[newPage]) {
        setLoading(false); // Instantly show data for prefetched pages
        setCallLogs(prefetchedPages[newPage]);
        setPage(newPage);
      } else {
        setLoading(true); // Only show loader if fetching new data
      setPage(newPage);
        // prefetchPages will run via useEffect
      }
    }
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    if (startPage > 1) {
      buttons.push(
        <button key="first" onClick={() => goToPage(1)} className={clsx("px-2 py-1 rounded text-xs font-medium", { "bg-gray-700 text-gray-300 hover:bg-gray-600": isDark, "bg-gray-200 text-gray-700 hover:bg-gray-300": !isDark, })}>1</button>
      );
      if (startPage > 2) {
        buttons.push(<span key="ellipsis1" className="px-1 text-gray-500">...</span>);
      }
    }
    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button key={i} onClick={() => goToPage(i)} className={clsx("px-2 py-1 rounded text-xs font-medium", {
          "bg-blue-600 text-white": i === page && isDark,
          "bg-blue-500 text-white": i === page && !isDark,
          "bg-gray-700 text-gray-300 hover:bg-gray-600": i !== page && isDark,
          "bg-gray-200 text-gray-700 hover:bg-gray-300": i !== page && !isDark,
        })}>{i}</button>
      );
    }
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(<span key="ellipsis2" className="px-1 text-gray-500">...</span>);
      }
      buttons.push(
        <button key="last" onClick={() => goToPage(totalPages)} className={clsx("px-2 py-1 rounded text-xs font-medium", { "bg-gray-700 text-gray-300 hover:bg-gray-600": isDark, "bg-gray-200 text-gray-700 hover:bg-gray-300": !isDark, })}>{totalPages}</button>
      );
    }
    return buttons;
  };

  return (
    <>
      {loading ? (
        <CallTableSkeleton title="Call Logs" />
      ) : (
    <motion.div
      className={clsx(
        "backdrop-blur-md shadow-lg rounded-xl p-4 sm:p-8 border w-full mx-auto my-6",
        {
          "bg-gray-800 bg-opacity-50 border-gray-700 text-gray-100": isDark,
          "bg-white border-gray-200 text-gray-900": !isDark,
        }
      )}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      style={{ minHeight: '80vh' }}
    >
      {/* Top Controls Row */}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <div className="w-full sm:w-auto flex-1 flex justify-start">
          <DateRangeSelector onDateRangeChange={handleDateRangeChange} />
        </div>
        <div className="w-full sm:w-auto flex-1 flex justify-end items-center gap-2">
          <button onClick={() => goToPage(page - 1)} disabled={page === 1} className={clsx("p-2 rounded-md", { "bg-gray-700 text-gray-300 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed": isDark, "bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed": !isDark, })}><ChevronLeft className="w-4 h-4" /></button>
          <div className="flex items-center gap-1">{renderPaginationButtons()}</div>
          <button onClick={() => goToPage(page + 1)} disabled={page === totalPages} className={clsx("p-2 rounded-md", { "bg-gray-700 text-gray-300 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed": isDark, "bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed": !isDark, })}><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg">
        <table className="w-full border-collapse">
          <thead className={clsx("sticky top-0 z-10", { "bg-gray-900": isDark, "bg-gray-100": !isDark, })}>
            <tr>
              {columns.map((column) => (
                <th key={column.id} className={clsx("px-4 py-3 text-left text-xs sm:text-sm font-semibold tracking-wide", { "text-gray-200": isDark, "text-gray-700": !isDark, })}>{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody className={clsx("divide-y", { "divide-gray-700": isDark, "divide-gray-200": !isDark, })}>
            <AnimatePresence>
                  {callLogs.length === 0 ? (
                <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <td colSpan={columns.length} className="px-6 py-12 text-center">
                    <span className={clsx("text-sm", { "text-gray-400": !isDark, "text-gray-500": isDark, })}>No call logs found for the selected date range.</span>
                  </td>
                </motion.tr>
              ) : (
                callLogs.map((row, index) => (
                  <motion.tr
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className={clsx("group transition-colors duration-150", {
                      "bg-gray-800": isDark && index % 2 === 1,
                      "bg-gray-900": isDark && index % 2 === 0,
                      "bg-white": !isDark && index % 2 === 0,
                      "bg-gray-50": !isDark && index % 2 === 1,
                      "hover:bg-blue-900/30": isDark,
                      "hover:bg-blue-100": !isDark,
                    })}
                  >
                    {columns.map((column) => (
                      <td key={column.id} className={clsx(
                        `px-4 py-3 text-xs sm:text-sm${column.id === 'date' ? ' whitespace-nowrap' : ''}`,
                        { "text-gray-300": isDark, "text-gray-700": !isDark, }
                      )}>
                        {column.id === 'date' ? (
                          row[column.id] ? new Date(row[column.id]).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'
                        ) : column.id === 'startTime' || column.id === 'endTime' ? (
                          row[column.id] ? row[column.id].slice(-8) : 'N/A'
                        ) : column.id === 'direction' ? (
                          row[column.id]
                            ? row[column.id].toLowerCase() === 'inbound'
                              ? 'Inbound'
                              : row[column.id].toLowerCase() === 'outbound'
                                ? 'Outbound'
                                : row[column.id]
                            : 'N/A'
                        ) : (
                          row[column.id] || 'N/A'
                        )}
                      </td>
                    ))}
                  </motion.tr>
                ))
              )}
            </AnimatePresence>
          </tbody>
        </table>
      </div>
    </motion.div>
      )}
    </>
  );
};

export default CallLogs;
